import { reactive, ref, toValue, watch, type MaybeRefOrGetter } from 'vue';

type StoreDispatch =
  | { type: 'open' }
  | { type: 'close' }
  | { type: 'search'; value: string }
  | { type: 'deselect'; id: string }
  | { type: 'add'; id: string }
  | { type: 'move-up'; id: string }
  | { type: 'move-down'; id: string }
  | { type: 'commit' };

export const createDefaultReviewerSelectorStore = (
  initialSelectedIds: MaybeRefOrGetter<string[] | undefined>,
) => {
  const isOpen = ref(false);
  const query = ref('');
  const committedIds = ref<string[]>([]);
  const draftIds = ref<string[]>([]);
  const dropdownValue = ref<string | null>(null);

  watch(
    () => toValue(initialSelectedIds),
    (ids) => {
      if (!isOpen.value) {
        committedIds.value = ids ?? [];
      }
    },
    { immediate: true },
  );

  const dispatch = (action: StoreDispatch): void => {
    switch (action.type) {
      case 'open':
        draftIds.value = [...committedIds.value];
        query.value = '';
        dropdownValue.value = null;
        isOpen.value = true;
        break;
      case 'close':
        isOpen.value = false;
        break;
      case 'search':
        query.value = action.value;
        break;
      case 'deselect':
        draftIds.value = draftIds.value.filter((id) => id !== action.id);
        break;
      case 'add':
        draftIds.value = [...draftIds.value, action.id];
        dropdownValue.value = null;
        break;
      case 'move-up': {
        const idx = draftIds.value.indexOf(action.id);
        if (idx > 0) {
          const next = [...draftIds.value];
          [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
          draftIds.value = next;
        }
        break;
      }
      case 'move-down': {
        const idx = draftIds.value.indexOf(action.id);
        if (idx < draftIds.value.length - 1) {
          const next = [...draftIds.value];
          [next[idx], next[idx + 1]] = [next[idx + 1], next[idx]];
          draftIds.value = next;
        }
        break;
      }
      case 'commit':
        committedIds.value = [...draftIds.value];
        break;
    }
  };

  return {
    data: reactive({
      get isOpen() {
        return isOpen.value;
      },
      get query() {
        return query.value;
      },
      get committedIds() {
        return committedIds.value;
      },
      get draftIds() {
        return draftIds.value;
      },
      get dropdownValue() {
        return dropdownValue.value;
      },
    }),
    dispatch,
  };
};
